/**
 * "Preview": the version being edited, as ridgewill.com/legal will show it
 * (Jon, 2026-10-16).
 *
 * ── The website's own renderer, not a copy of it ─────────────────────
 *
 * `@ridgewill/legal-render` is the code ridgewill.com's Pages Function renders
 * with, so the preview cannot drift from the site: a markdown feature the site
 * does not support looks wrong here too, which is the point. It links the
 * site's stylesheet from `siteBase`, so this build's site, or ridgewill.com
 * where the build has none (staging), and it puts its own "Preview, not
 * published" notice at the top of the page.
 *
 * ── A Blob URL in a new tab ──────────────────────────────────────────
 *
 * Opened synchronously inside the click, so no popup blocker objects. A blob:
 * document inherits this portal's CSP, which csp.ts widens for the site's
 * stylesheet and fonts and nothing else. The site's script stays blocked,
 * which it is built to survive, and which is right for a page made of text
 * someone typed.
 */
import { renderPreview, type PreviewInput } from '@ridgewill/legal-render';
import { SITE_URL, type AgreementKind } from './agreement-public.js';
import { toInstant, type VersionForm } from './agreement-drafts.js';

export const PREVIEW_SITE_BASE = SITE_URL || 'https://ridgewill.com';

export type PreviewAgreement = { name: string; kind: AgreementKind; publicVersioned: boolean };

/** renderPreview's input for the form as it stands, saved or not. Blank
 *  fields are left for the renderer, which shows "draft" and today's date. */
export function previewInput(a: PreviewAgreement, f: VersionForm, siteBase = PREVIEW_SITE_BASE): PreviewInput {
  return {
    name: a.name,
    kind: a.kind,
    versioned: a.publicVersioned,
    siteBase,
    version: {
      version: f.version.trim(),
      effectiveAt: toInstant(f.effectiveAt) ?? '',
      summary: f.summary.trim(),
      content: f.content,
      contentFormat: 'markdown',
    },
  };
}

/** The tab says it too, for when the notice has scrolled away or the tab is
 *  one of several. */
export function markPreviewTitle(html: string): string {
  return html.replace(/<title>/i, '<title>Preview, not published: ');
}

export function openPreview(a: PreviewAgreement, f: VersionForm): void {
  const html = markPreviewTitle(renderPreview(previewInput(a, f)));
  // Not revoked: the tab may be reloaded, and a preview is a few kilobytes
  // that go when the portal's tab does.
  const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
  window.open(url, '_blank');
}
